import i18n from 'helpers/i18n';
import messages from './messages';
import NodeForm from './NodeForm';
import { useState } from 'react';
import { clsx } from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { IconButton } from 'components/Organization/IconButton';
import { toastError, toastSuccess } from 'components/Organization/Toast';
import { DeleteNodeModal } from 'components/Organization/DeleteNodeModal';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import {
  createAccountNode,
  deleteAccountNode,
  updateAccountNode
} from 'actions/accounts';

import {
  useNode,
  Node,
  NodeName,
  NodeChildren,
  NodeDisclosureButton
} from 'components/Organization/HierarchyBuilder';

export function EditableNode() {
  const { depth, node, accountId, setIsOpen } = useNode();
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();
  const [nodeType, setNodeType] = useState('GenericNode');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const handleEditNode = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    setIsEditing(true);
  };

  const handleRemoveNode = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    setIsDeleting(true);
  };

  const handleDeleteNodeConfirm = async () => {
    try {
      await deleteAccountNode(accountId, node.id);
      setIsDeleting(false);
      toastSuccess({
        message: i18n.ft(messages.nodeRemoved, { nodeName: node.name })
      });

      queryClient.invalidateQueries({
        queryKey: ['accounts', accountId, 'hierarchy']
      });
    } catch (error: any) {
      if (error.response.status === 403) {
        toastError({
          message: i18n.ft(messages.deleteNotAuthorized)
        });
      }
    }
  };

  const handleAddNode = (event: React.SyntheticEvent, nodeType: string) => {
    event.stopPropagation();
    setNodeType(nodeType);
    setIsAdding(true);
  };

  const handleSaveNode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = event.currentTarget.elements as any;
    const nodeName = formData.name.value;

    if (!nodeName) {
      return;
    }

    try {
      await createAccountNode(accountId, {
        node: {
          names: [nodeName],
          type: nodeType,
          parent_id: node.id
        }
      });

      setIsOpen(true);
      setIsAdding(false);
      toastSuccess({
        message: i18n.ft(messages.nodeAdded, {
          nodeName
        })
      });

      queryClient.invalidateQueries({
        queryKey: ['accounts', accountId, 'hierarchy']
      });
    } catch (error: any) {
      if (error.response.status === 403) {
        toastError({
          message: i18n.ft(messages.createNotAuthorized, {
            parentNodeName: node.name
          })
        });

        return;
      }

      // TODO: Connect to API error after API is updated
      toastError({
        message: i18n.ft(messages.nodeError, {
          nodeName
        })
      });
    }
  };

  function getAddNodeType() {
    const firstChild = node.children[0];
    return firstChild.type;
  }

  function getAddNodeLabel() {
    const firstChild = node.children[0];

    return firstChild.type === 'GenericNode'
      ? i18n.ft(messages.addCenter)
      : i18n.ft(messages.addClassroom);
  }

  const handleFieldSave = async (
    event: React.FormEvent<HTMLFormElement>,
    value: any
  ) => {
    event.preventDefault();
    if (!value) {
      return;
    }

    try {
      await updateAccountNode(accountId, node.id, {
        node: { ...node, name: value }
      });

      setIsOpen(true);
      setIsEditing(false);
      toastSuccess({
        message: i18n.ft(messages.nodeEdited, {
          nodeName: value
        })
      });

      queryClient.invalidateQueries({
        queryKey: ['accounts', accountId, 'hierarchy']
      });
    } catch (error: any) {
      if (error.response.status === 403) {
        toastError({
          message: i18n.ft(messages.editNotAuthorized)
        });

        return;
      }

      toastError({
        message: i18n.ft(messages.nodeEditError, {
          nodeName: value
        })
      });
    }
  };

  function getRemoveNodeLabel() {
    return node.type === 'GenericNode'
      ? i18n.ft(messages.removeCenter)
      : i18n.ft(messages.removeClassroom);
  }

  function getNodeTypeName() {
    return node.type === 'GenericNode'
      ? i18n.ft(messages.center)
      : i18n.ft(messages.classroom);
  }

  return (
    <>
      <DeleteNodeModal
        open={isDeleting}
        nodeTypeName={getNodeTypeName()}
        onConfirm={handleDeleteNodeConfirm}
        onClose={() => setIsDeleting(false)}
      />

      {isEditing ? (
        <NodeForm
          depth={depth}
          setIsAvailable={setIsEditing}
          type="edit"
          onSave={handleFieldSave}
          value={node.name}
        />
      ) : (
        <Node
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
        >
          <div className="flex items-center gap-3">
            {depth > 1 && <i className="fa-solid fa-arrow-turn-down-right" />}
            <NodeName />
          </div>

          <div className="flex items-center gap-8">
            <div
              className={clsx(
                'flex items-center gap-4',
                showActions ? 'opacity-100' : 'opacity-0'
              )}
            >
              <IconButton
                label={i18n.ft(messages.editName)}
                icon="fa-solid fa-pencil"
                onClick={handleEditNode}
              />

              {node.type === 'GenericNode' && node.children.length > 0 && (
                <IconButton
                  label={getAddNodeLabel()}
                  icon="fa-solid fa-plus"
                  onClick={event => handleAddNode(event, getAddNodeType())}
                />
              )}

              {node.type === 'GenericNode' && node.children.length === 0 && (
                <Menu>
                  <MenuButton>
                    <IconButton
                      icon="fa-solid fa-plus"
                      label={i18n.ft(messages.addNewNode)}
                    />
                  </MenuButton>
                  <MenuItems
                    modal={false}
                    anchor="bottom end"
                    className="w-52 rounded-xl border-2 border-action bg-white p-1 focus:outline-none"
                  >
                    <MenuItem>
                      <button
                        onClick={event => handleAddNode(event, 'GenericNode')}
                        className="font-sans text-black flex w-full rounded-lg p-2 data-[focus]:bg-action-tint"
                      >
                        {i18n.ft(messages.addCenter)}
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <button
                        onClick={event => handleAddNode(event, 'Classroom')}
                        className="font-sans text-black flex w-full rounded-lg p-2 data-[focus]:bg-action-tint"
                      >
                        {i18n.ft(messages.addClassroom)}
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              )}

              {node.children.length === 0 && (
                <IconButton
                  label={getRemoveNodeLabel()}
                  icon="fa-solid fa-trash-can"
                  onClick={handleRemoveNode}
                />
              )}
            </div>

            <NodeDisclosureButton />
          </div>
        </Node>
      )}

      {isAdding && (
        <NodeForm
          depth={depth}
          setIsAvailable={setIsAdding}
          type="add"
          onSave={handleSaveNode}
        />
      )}

      <NodeChildren />
    </>
  );
}
